import React from 'react';
import { CannulationBarData, CannulationBarSegment } from '../../models/CannulationScrewPlacementAdapter';

export interface CannulationToasterContentProps {
  barData: CannulationBarData;
  onWatchSegment: (segment: CannulationBarSegment) => void;
}

/**
 * Custom toaster content for the Cannulation & Screw Placement chart.
 * Displays the locus, total efficiency time, and a breakdown of each activity
 * with Watch buttons for video playback.
 */
export function CannulationToasterContent({
  barData,
  onWatchSegment,
}: CannulationToasterContentProps): React.JSX.Element {
  return (
    <div className="flex flex-col w-full">
      {/* Header: Locus + Total Efficiency */}
      <div
        className="text-lg mb-1.5"
        style={{
          fontFamily: 'Cabin, sans-serif',
          fontWeight: 600,
          fontSize: '18px',
        }}
      >
        <span style={{ color: 'var(--T2)' }}>
          {barData.locus}
        </span>
        <span className="text-high-emphasis-font">
          {' '}Total Efficiency - {barData.displayTotalTime}
        </span>
      </div>

      {/* Activity breakdown */}
      <div className="flex flex-col">
        {barData.segments.map((segment) => (
          <React.Fragment key={segment.activityType}>
            {/* Divider line */}
            <div
              className="w-full h-px"
              style={{ backgroundColor: 'var(--border)' }}
            />

            {/* Activity row */}
            <div className="flex items-center justify-between py-3 gap-4">
              <div className="flex items-center gap-1 min-w-0">
                <span className="text-base font-medium text-high-emphasis-font whitespace-nowrap overflow-hidden text-ellipsis">
                  {segment.label}
                </span>
                <span className="text-base text-high-emphasis-font">
                  {' '}-
                </span>
                <span
                  className="text-base font-medium"
                  style={{ color: segment.color }}
                >
                  {segment.displayDuration}
                </span>
              </div>

              {/* Watch button */}
              <button
                type="button"
                onClick={() => onWatchSegment(segment)}
                disabled={!segment.videoInfo?.videoIsValid}
                className="flex items-center gap-2 px-4 py-2 rounded-[var(--radius)] text-sm font-medium text-high-emphasis-font bg-transparent hover:bg-surface-100 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                style={{ border: '1px solid var(--border)' }}
                aria-label={`Watch ${segment.label} video`}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
                Watch
              </button>
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

export default CannulationToasterContent;
